"use client";

/**
 * 桌面台词汇表 —— DESIGN.md §8 的实装，**全部建在 shadcn 原语之上**。
 *
 * 这一层只做两件事：把 shadcn 的通用组件组合成本项目的说法（「一节表单」「一格名额」
 * 「一条队列」），以及把 §8 的密度写进类名（44px 行、14/16px 字、8px 间距）。
 * 原语保留通用样式；页面宽度和首轮工作区形制集中在 workspace.css。
 *
 * 颜色一律走 shadcn 的语义类（bg-card / text-muted-foreground / border-border…），
 * 而那些语义变量在 globals.css 里指向本项目令牌，所以配色仍然是 §2 的建蓝／纸白／墨青。
 * 需要状态色时用 var(--success) 一类的令牌，不写死色值——CI 的 check:tokens 盯着。
 */

import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from "react";
import { Alert, AlertDescription } from "../ui/alert";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { cn } from "@/lib/utils";

export { Button } from "../ui/button";
export { Input } from "../ui/input";
export { Textarea } from "../ui/textarea";

type Tone = "neutral" | "brand" | "success" | "warning" | "danger";

// 凭据 · 套色（DESIGN.md §1.1「状态靠形」）：语气映射到四形，颜色一律是墨——
// brand（办理中、当前）= 实线框，warning（等人动手）= 虚线框，danger（被拒、失败、阻断）= 反白，
// success（完成、有结局）= 实底加勾，neutral = 纯文字。形状定义在 app/taose.css。
const STATE_CLASS: Record<Tone, string> = {
  neutral: "state state--plain",
  brand: "state state--active",
  success: "state state--done",
  warning: "state state--pending",
  danger: "state state--blocked"
};
// 提示条：必须处理的（warning / danger）是墨框；其余是沉底说明。
const BANNER_CLASS: Record<Tone, string> = {
  neutral: "callout callout--note",
  brand: "callout callout--note",
  success: "callout callout--note",
  warning: "callout callout--alert",
  danger: "callout callout--alert"
};

/* ─────────────────────────────── 骨架 ─────────────────────────────── */

/** 一般页保持阅读宽度；宽列表显式启用 wide（DESIGN.md §8.4）。 */
export function Desk({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  return <div className={cn("desk-page", wide && "desk-page--wide")}>{children}</div>;
}

/** 对账及待迁移页面使用的双栏骨架；新列表按需要打开详情。 */
export function DeskSplit({ children }: { children: ReactNode }) {
  return (
    <div className="grid items-start gap-4 lg:grid-cols-[var(--d-queue)_minmax(0,1fr)]">
      {children}
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions
}: {
  eyebrow: string;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="desk-page-header">
      <div>
        <p className="text-xs font-semibold tracking-wider text-primary">{eyebrow}</p>
        <h1 className="mt-2 text-[length:var(--d-fs-page)] leading-tight font-semibold tracking-tight">
          {title}
        </h1>
        {description ? (
          <p className="mt-3 max-w-[78ch] text-sm leading-relaxed text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
    </header>
  );
}

/** 这一台此刻的两三个数。不做「政绩看板」式的大数字（产品宪法）。 */
export function StatRow({ children }: { children: ReactNode }) {
  return (
    <Card className="mb-6 py-6">
      <CardContent className="flex gap-12 px-8">{children}</CardContent>
    </Card>
  );
}

export function Stat({ value, label, hint }: { value: ReactNode; label: string; hint?: string }) {
  return (
    <div className="flex flex-col">
      {/* 等宽数字（§8.3）：名额、席位、人数都是要竖着比的。
          字号给到 30px——台面上得有一个够分量的锚点，否则满屏 13px 灰字，
          眼睛落不下来，这正是「不大气」的来源之一。 */}
      <span className="text-3xl leading-none font-semibold tabular-nums">{value}</span>
      <span className="mt-2 text-sm font-medium">{label}</span>
      {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
    </div>
  );
}

export function DeskCard({
  title,
  description,
  actions,
  children,
  className
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn("mb-4", className)}>
      {title ? (
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
          {description ? (
            <CardDescription className="max-w-[62ch] leading-relaxed">
              {description}
            </CardDescription>
          ) : null}
          {actions ? <CardAction className="flex items-center gap-2">{actions}</CardAction> : null}
        </CardHeader>
      ) : null}
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/* ─────────────────────────────── 表单 ─────────────────────────────── */

/** 表单的一节。十六个字段平铺是让人填错的写法（§8.7）。 */
export function FormSection({
  id,
  step,
  title,
  description,
  children
}: {
  id?: string;
  step: string;
  title: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <fieldset
      id={id}
      className="desk-form-section border-0 p-0 not-first:mt-6 not-first:border-t not-first:pt-6"
    >
      <legend className="flex items-center gap-2 p-0 text-base font-semibold">
        <span className="grid size-6 place-items-center rounded-md bg-[var(--brand-soft)] text-xs font-semibold text-[var(--brand)]">
          {step}
        </span>
        {title}
      </legend>
      {description ? (
        <p className="mt-2 max-w-[64ch] text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      ) : null}
      <div className="mt-4">{children}</div>
    </fieldset>
  );
}

export function FieldGrid({ children }: { children: ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}

export function Field({
  label,
  hint,
  htmlFor,
  wide,
  children
}: {
  label: string;
  hint?: ReactNode;
  htmlFor?: string;
  wide?: boolean;
  children: ReactNode;
}) {
  const generatedId = useId();
  const fieldId = htmlFor ?? generatedId;
  const hintId = `${fieldId}-hint`;
  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<{ id?: string; "aria-describedby"?: string }>, {
        id: fieldId,
        ...(hint ? { "aria-describedby": hintId } : {})
      })
    : children;
  return (
    <div className={cn("flex min-w-0 flex-col gap-2", wide && "sm:col-span-2")}>
      <Label htmlFor={fieldId} className="text-sm font-medium">
        {label}
      </Label>
      {control}
      {hint ? (
        <span id={hintId} className="text-xs leading-relaxed text-muted-foreground">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/** 简单选项列表用这个：把 shadcn Select 的三段式收成一行。 */
export function SelectField({
  id,
  "aria-describedby": describedBy,
  value,
  onChange,
  placeholder,
  options,
  disabled
}: {
  id?: string;
  "aria-describedby"?: string;
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  options: Array<{ value: string; label: string }>;
  disabled?: boolean;
}) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} aria-describedby={describedBy} className="w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * 关键多选：可勾选列表，不用原生 <select multiple>（§8.8）。
 *
 * 原生多选要按住 Ctrl 点选、误点一下清空全部、选中了什么得眯着眼数高亮行；
 * 而它管的偏偏是「这场活动发给谁」——选错了活动照发，
 * 错在哪儿要等会员端空了才发现。
 */
export function CheckList({
  options,
  value,
  onChange,
  emptyText,
  quickActions
}: {
  options: Array<{ id: string; label: string; note?: string }>;
  value: string[];
  onChange: (next: string[]) => void;
  emptyText: string;
  quickActions?: ReactNode;
}) {
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <div className="rounded-md border">
      <div className="flex items-center justify-between gap-3 border-b px-3 py-2">
        <span className="text-xs font-semibold tabular-nums">已选 {value.length} 个</span>
        {quickActions ? <span className="flex gap-1">{quickActions}</span> : null}
      </div>
      {options.length === 0 ? (
        <p className="px-3 py-3 text-xs text-muted-foreground">{emptyText}</p>
      ) : (
        <div className="max-h-60 overflow-y-auto overscroll-contain">
          <div className="p-1">
            {options.map((option) => (
              <label
                key={option.id}
                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 hover:bg-muted"
              >
                <Checkbox
                  checked={value.includes(option.id)}
                  onCheckedChange={() => toggle(option.id)}
                />
                <span className="text-sm">{option.label}</span>
                {option.note ? (
                  <span className="text-xs font-semibold text-primary">{option.note}</span>
                ) : null}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ButtonRow({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}

/**
 * 危险操作的行内确认（§8.10）。
 * 不用 window.confirm / window.prompt——原生弹窗不受样式约束，也讲不清后果。
 * 退路永远留着：confirmDisabled 只锁「确认」，不锁「算了」。
 */
export function ConfirmPanel({
  title,
  consequence,
  children,
  onCancel,
  onConfirm,
  confirmLabel,
  busy,
  confirmDisabled
}: {
  title: string;
  consequence: string;
  children?: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  busy?: boolean;
  confirmDisabled?: boolean;
}) {
  return (
    <div className="mt-4 rounded-md bg-[var(--danger-soft)] p-4">
      <p className="text-sm font-semibold">{title}</p>
      <p className="mt-2 mb-3 text-xs leading-relaxed text-muted-foreground">{consequence}</p>
      {children ? <div className="mb-3">{children}</div> : null}
      <ButtonRow>
        <Button
          variant="destructive"
          size="sm"
          disabled={busy || confirmDisabled}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
        <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
          算了
        </Button>
      </ButtonRow>
    </div>
  );
}

/* ─────────────────────────────── 状态与说明 ─────────────────────────────── */

/** 颜色不单独承担状态表达，标签里永远同时有状态词（§2 末条）。 */
export function StatusTag({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <Badge variant="secondary" className={cn("font-semibold", STATE_CLASS[tone])}>
      {children}
    </Badge>
  );
}

export function Banner({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <Alert className={cn("mb-4", BANNER_CLASS[tone])}>
      <AlertDescription className="text-current">{children}</AlertDescription>
    </Alert>
  );
}

/** 制度说明：为什么这里只给这些、为什么不给那些。 */
export function Note({ children }: { children: ReactNode }) {
  return (
    <p className="mt-3 border-l-2 border-[var(--line-brand)] pl-3 text-xs leading-relaxed text-muted-foreground">
      {children}
    </p>
  );
}

export function EmptyState({
  title,
  description,
  action
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="px-6 py-12 text-center">
      <p className="text-sm font-semibold">{title}</p>
      {description ? (
        <p className="mx-auto mt-2 max-w-[44ch] text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

/* ─────────────────────────────── 队列与记录 ─────────────────────────────── */

export function Queue({ children }: { children: ReactNode }) {
  return <ul className="list-none px-2 pb-2">{children}</ul>;
}

export function QueueItem({
  active,
  onSelect,
  tag,
  status,
  title,
  meta
}: {
  active: boolean;
  onSelect: () => void;
  tag: ReactNode;
  status: ReactNode;
  title: ReactNode;
  meta: ReactNode;
}) {
  return (
    <li className="not-first:border-t">
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "flex w-full flex-col gap-1.5 rounded-md px-3 py-3 text-left transition-colors",
          "focus-visible:ring-ring/50 focus-visible:ring-2 focus-visible:outline-none",
          active ? "bg-[var(--brand-soft)]" : "hover:bg-muted"
        )}
      >
        <span className="flex items-start justify-between gap-3">
          <span className="text-sm leading-snug font-semibold">{title}</span>
          <span className="shrink-0">{status}</span>
        </span>
        <span className="text-xs tabular-nums text-muted-foreground">
          {tag}
          {tag && meta ? " · " : ""}
          {meta}
        </span>
      </button>
    </li>
  );
}

/** 一行一条记录：左边是事实，右边是能对它做的动作。行高按 §8.4 的 44px。 */
export function RecordList({ children }: { children: ReactNode }) {
  return <ul className="list-none p-0">{children}</ul>;
}

export function RecordRow({
  title,
  meta,
  actions
}: {
  title: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <li className="flex min-h-11 items-center justify-between gap-3 py-2 not-first:border-t">
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-semibold">{title}</span>
        {meta ? <span className="text-xs tabular-nums text-muted-foreground">{meta}</span> : null}
      </span>
      {actions ? <span className="flex shrink-0 items-center gap-2">{actions}</span> : null}
    </li>
  );
}

/** 事实表：标签在左，值在右，数字等宽（§8.3）。 */
export function Facts({ children }: { children: ReactNode }) {
  return <dl className="m-0 grid">{children}</dl>;
}

export function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-baseline gap-4 py-2 not-first:border-t">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="m-0 text-sm leading-relaxed tabular-nums">{children}</dd>
    </div>
  );
}

/**
 * 筛选切换。默认按内容宽度排，不铺满——四个两字标签摊到 1000px 宽，
 * 每格之间隔着一大片空，读起来不像一组开关。要铺满时显式传 full。
 */
export function Segmented({
  options,
  value,
  onChange,
  full
}: {
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (next: string) => void;
  full?: boolean;
}) {
  return (
    <Tabs value={value} onValueChange={onChange}>
      <TabsList className={full ? "w-full" : undefined}>
        {options.map((option) => (
          <TabsTrigger key={option.value || "ALL"} value={option.value}>
            {option.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
